import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  ClipboardCheck,
  Wallet,
  FileText,
  ArrowRight,
} from 'lucide-react';

export default function HowItWorks() {
  const { t } = useTranslation();

  const steps = t('public.howItWorks.steps', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;

  const stepIcons = [
    Building2,
    Users,
    ClipboardCheck,
    Wallet,
    FileText,
  ];

  const stepColors = [
    'bg-blue-500/10 text-blue-500',
    'bg-green-500/10 text-green-500',
    'bg-purple-500/10 text-purple-500',
    'bg-orange-500/10 text-orange-500',
    'bg-pink-500/10 text-pink-500',
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-primary/10 via-background to-background py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
              {t('public.howItWorks.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              Get started with Tickally in 5 simple steps
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8 sm:space-y-12">
              {steps.map((step, index) => {
                const Icon = stepIcons[index];
                const colorClass = stepColors[index];
                const isEven = index % 2 === 1;

                return (
                  <div
                    key={index}
                    className={`flex flex-col ${
                      isEven ? 'lg:flex-row-reverse' : 'lg:flex-row'
                    } gap-6 lg:gap-12 items-center`}
                  >
                    {/* Step Number & Icon */}
                    <div className="flex-shrink-0">
                      <Card className="w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center border-2 border-primary/20">
                        <CardContent className="p-0 flex flex-col items-center justify-center">
                          <div className={`w-14 h-14 sm:w-16 sm:h-16 ${colorClass} rounded-2xl flex items-center justify-center mb-2`}>
                            <Icon className="h-7 w-7 sm:h-8 sm:w-8" />
                          </div>
                          <span className="text-3xl sm:text-4xl font-bold text-primary">
                            {index + 1}
                          </span>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Connector Line (Desktop) */}
                    {index < steps.length - 1 && (
                      <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 mt-40">
                        <div className="w-0.5 h-12 bg-gradient-to-b from-primary/50 to-transparent" />
                      </div>
                    )}

                    {/* Step Content */}
                    <div className={`flex-1 text-center ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                      <h3 className="text-xl sm:text-2xl font-bold mb-3">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-base sm:text-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features Highlight */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Why Organizations Choose Tickally
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Designed specifically for informal groups and community organizations
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Easy Member Management</h3>
                <p className="text-sm text-muted-foreground">
                  Add, edit, and organize members with a simple interface. Import existing member lists with CSV.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                  <ClipboardCheck className="h-5 w-5 text-green-500" />
                </div>
                <h3 className="font-semibold mb-2">Quick Attendance</h3>
                <p className="text-sm text-muted-foreground">
                  Mark attendance in seconds with our meeting mode. View attendance history and statistics.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Wallet className="h-5 w-5 text-blue-500" />
                </div>
                <h3 className="font-semibold mb-2">Smart Dues Collection</h3>
                <p className="text-sm text-muted-foreground">
                  Automatic allocation to backlogs, current month, or prepay. Track who owes what at a glance.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="h-5 w-5 text-purple-500" />
                </div>
                <h3 className="font-semibold mb-2">Financial Reports</h3>
                <p className="text-sm text-muted-foreground">
                  Generate comprehensive financial summaries. Export data for external accounting.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Mobile Friendly</h3>
                <p className="text-sm text-muted-foreground">
                  Works perfectly on phones, tablets, and computers. Use as a PWA or convert to native apps.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center mb-4">
                  <svg className="h-5 w-5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold mb-2">Multi-Language</h3>
                <p className="text-sm text-muted-foreground">
                  Support for English, French, Spanish, Italian, German, and Mandarin Chinese.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-foreground mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Join thousands of organizations already using Tickally to manage their members and finances.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/org-signup">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                {t('public.home.orgs.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/member-signup">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                {t('public.home.members.cta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
